import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Inbox, Link2, RefreshCw, Search, Trash2 } from "lucide-react";
import { formatMinute } from "@convex/lib/time";
import {
  cardClass,
  errorMessage,
  inputClass,
  monoMetaClass,
  secondaryButtonClass,
} from "@/lib/ui";

/**
 * The tray for recordings that no lesson owns - the Record button pressed on an
 * unplanned session, a lecture that ran far enough from its slot that the
 * overlap score missed, or a lesson deleted after the fact.
 *
 * Auto-matching (PLAN.md §9) has already run on finalize; everything here is
 * what it declined to guess at. The picker offers the same scores the automatic
 * pass computed, so a manual choice is made against the same numbers.
 */
export default function Unassigned() {
  const recordings = useQuery(api.recordings.listUnassigned, {});
  const rematch = useMutation(api.matching.rematch);
  const [error, setError] = useState<string | null>(null);
  const [rematching, setRematching] = useState(false);
  const [rematchResult, setRematchResult] = useState<string | null>(null);

  async function handleRematch() {
    setRematching(true);
    setError(null);
    setRematchResult(null);
    try {
      const result = await rematch({});
      setRematchResult(
        `${result.matched} of ${result.scanned} attached.`,
      );
    } catch (caught) {
      setError(errorMessage(caught, "Matching failed."));
    } finally {
      setRematching(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="relative pb-4">
        <h1 className="text-2xl font-light tracking-[-0.05em] text-[#222] sm:text-3xl dark:text-[#ededeb]">
          Unassigned
        </h1>
        <p className="mt-1 font-mono text-xs text-neutral-600 opacity-60 dark:text-neutral-400">
          Recordings with no lesson attached
        </p>
        <div className="meniscus pointer-events-none absolute bottom-0 left-0 h-[1px] w-full opacity-40" />
      </div>

      {error !== null && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {recordings === undefined && (
        <p className={`mt-6 ${monoMetaClass}`}>Loading…</p>
      )}

      {recordings !== undefined && recordings.length === 0 && (
        <div className={`mt-6 flex items-center gap-3 ${cardClass}`}>
          <Inbox className="h-4 w-4 text-neutral-400" />
          <p className={monoMetaClass}>
            Nothing here. Recordings started from a lesson are attached automatically.
          </p>
        </div>
      )}

      {recordings !== undefined && recordings.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className={secondaryButtonClass}
            disabled={rematching}
            onClick={() => void handleRematch()}
          >
            <span className="flex items-center gap-1.5">
              <RefreshCw className={`h-3.5 w-3.5 ${rematching ? "animate-spin" : ""}`} />
              {rematching ? "Matching…" : "Re-run matching"}
            </span>
          </motion.button>
          {rematchResult !== null && (
            <span className={monoMetaClass}>{rematchResult}</span>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        <AnimatePresence initial={false}>
          {(recordings ?? []).map((recording) => (
            <RecordingRow
              key={recording._id}
              recording={recording}
              onError={setError}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

type Recording = NonNullable<
  ReturnType<typeof useQuery<typeof api.recordings.listUnassigned>>
>[number];

function RecordingRow({
  recording,
  onError,
}: {
  recording: Recording;
  onError: (message: string | null) => void;
}) {
  const assignLesson = useMutation(api.recordings.assignLesson);
  const remove = useMutation(api.recordings.remove);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  // Empty search means "the nearest lessons in time"; typing widens it to a
  // name lookup across the whole materialized window.
  const candidates = useQuery(api.matching.candidates, {
    id: recording._id,
    limit: search.trim() === "" ? 5 : 20,
    search: search.trim() === "" ? undefined : search.trim(),
  });

  async function assign(lessonId: Id<"lessons">) {
    setBusy(true);
    onError(null);
    try {
      await assignLesson({ id: recording._id, lessonId });
    } catch (caught) {
      onError(errorMessage(caught, "Could not attach the recording."));
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    setBusy(true);
    onError(null);
    try {
      await remove({ id: recording._id });
    } catch (caught) {
      onError(errorMessage(caught, "Could not delete the recording."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className={cardClass}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm text-[#222] dark:text-[#ededeb]">
          {new Date(recording.startedAt).toLocaleString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <span className={monoMetaClass}>{recording.state}</span>
        {recording.fileName !== undefined && (
          <span
            className={`${monoMetaClass} max-w-[220px] truncate`}
            title={recording.fileName}
          >
            {recording.fileName}
          </span>
        )}
        {recording.durationMs !== undefined && (
          <span className={monoMetaClass}>{minutesOf(recording.durationMs)} min</span>
        )}
        {recording.audioBytes !== undefined && (
          <span className={monoMetaClass}>{megabytes(recording.audioBytes)}</span>
        )}
        {recording.gapSeqs.length > 0 && (
          <span className="font-mono text-xs text-[#F59E0B]">
            {recording.gapSeqs.length} missing chunk
            {recording.gapSeqs.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {recording.error !== undefined && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{recording.error}</p>
      )}

      <div className="relative mt-3 flex items-center">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 shrink-0 text-neutral-400" />
        <input
          type="search"
          value={search}
          placeholder="Nearest lessons - type to search by name"
          onChange={(event) => setSearch(event.target.value)}
          className={`${inputClass} w-full py-1.5 !pl-8 text-xs`}
        />
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        {candidates === undefined && <span className={monoMetaClass}>Loading…</span>}
        {candidates !== undefined && candidates.length === 0 && (
          <span className={monoMetaClass}>
            {search.trim() === ""
              ? "No lessons near this recording."
              : "No lesson matches that name."}
          </span>
        )}
        {(candidates ?? []).map((lesson) => (
          <button
            key={lesson._id}
            type="button"
            disabled={busy}
            onClick={() => void assign(lesson._id)}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-left text-sm transition-colors hover:border-[#d1d1cf] hover:bg-[#fafaf8] disabled:cursor-not-allowed disabled:opacity-40 dark:hover:border-[#2a2a28] dark:hover:bg-[#1a1a19] min-w-0"
          >
            <Link2 className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: lesson.color }}
            />
            <span className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400 shrink-0">
              {lesson.date} {formatMinute(lesson.startMinute)}
            </span>
            <span className="min-w-0 flex-1 truncate text-[#222] dark:text-[#ededeb]">
              {lesson.shortCode !== undefined && `${lesson.shortCode} `}
              {lesson.name}
            </span>
            {lesson.cancelled && (
              <span className="font-mono text-[10px] uppercase text-neutral-400 shrink-0">
                cancelled
              </span>
            )}
            <span className={`ml-auto shrink-0 ${scoreClass(lesson.score)}`}>
              {Math.round(lesson.score * 100)}%
            </span>
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={secondaryButtonClass}
          disabled={busy}
          onClick={() => void discard()}
        >
          <span className="flex items-center gap-1.5">
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </span>
        </button>
      </div>
    </motion.div>
  );
}

/** Green once the score would have matched automatically, amber below it. */
function scoreClass(score: number): string {
  const base = "font-mono text-[11px]";
  if (score >= 0.8) return `${base} text-[#22C55E]`;
  if (score >= 0.5) return `${base} text-[#F59E0B]`;
  return `${base} text-neutral-400`;
}

function minutesOf(durationMs: number): number {
  return Math.max(1, Math.round(durationMs / 60_000));
}

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
